import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import config from '@/config';
import fetchWrapper from '@/services/fetchWrapper.service';

// Optional: enable with REACT_APP_ENABLE_LE_COPY=true to hit editions endpoints
const LE_COPY_ENABLED = process.env.REACT_APP_ENABLE_LE_COPY === 'true';

function logToSentry(error) {
  if (typeof window !== 'undefined' && window.Sentry?.captureException) {
    window.Sentry.captureException(error);
  } else {
    // Surface the issue during development without breaking the UI.
    console.error(error);
  }
}

export default function useEditionStatus(productId, options = {}) {
  const { enabled = true, initialData = null } = options;
  const [state, setState] = useState(() => ({
    data: initialData,
    loading: Boolean(LE_COPY_ENABLED && enabled && productId && !initialData),
    error: null,
  }));

  const requestIdRef = useRef(0);

  const resetState = useCallback(() => {
    setState({
      data: initialData,
      loading: false,
      error: null,
    });
  }, [initialData]);

  const fetchStatus = useCallback(async () => {
    if (!LE_COPY_ENABLED || !enabled || !productId) {
      resetState();
      return null;
    }

    const currentRequestId = requestIdRef.current + 1;
    requestIdRef.current = currentRequestId;

    setState((prev) => ({
      ...prev,
      loading: true,
      error: null,
    }));

    try {
      const endpoint = `${config.apis.api.url}/api/editions/${encodeURIComponent(productId)}/status`;
      const { response, data } = await fetchWrapper.get(endpoint, { injectToken: true });

      if (!response?.ok) {
        throw new Error(`Failed to load edition status (${response?.status ?? 'unknown'})`);
      }

      if (requestIdRef.current !== currentRequestId) {
        return data ?? null;
      }

      setState({
        data: data ?? null,
        loading: false,
        error: null,
      });

      return data ?? null;
    } catch (error) {
      logToSentry(error);

      if (requestIdRef.current !== currentRequestId) {
        return null;
      }

      setState({
        data: null,
        loading: false,
        error,
      });
      return null;
    }
  }, [enabled, productId, resetState]);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  const memoized = useMemo(() => ({
    data: state.data,
    loading: state.loading,
    error: state.error,
    refresh: fetchStatus,
    hasData: Boolean(state.data),
  }), [fetchStatus, state.data, state.error, state.loading]);

  return memoized;
}
